import TimeSignature from './TimeSignature';
import {
  beatGridOf,
  beatIndexAtOrBefore,
  beatStartInBars,
  beatsPerBar,
  isDownbeat,
} from './MeterTiming';

/**
 * The count-in for ungraded play (Play Along): a fixed number of metronome
 * clicks on the beats immediately before the first note the player will hear
 * or play, then the music starts on its own. Graded play keeps its open-ended
 * "starts when you play" wait and never uses this.
 *
 * Positions here are in grid bars: bars from the metronome's beat 0, which is
 * always a downbeat. A transport position in measures becomes grid bars by
 * adding the pickup offset in measures (a one-beat pickup in 4/4 starts the
 * piece at grid bar 0.75, so its first note is beat 4). The clicks are the
 * same clicks the playback metronome schedules on the audio clock, so they
 * carry its downbeat accent and sit on its beat grid.
 *
 * The rule needs no special case for pickups or offbeat entries: the clicks
 * are the N beats that start strictly before the entry. An entry on a beat
 * gets N whole beats before it; an entry on an "and" (or an "e" / "a") gets
 * the N beats before it, the last of which falls inside the entry's own beat.
 */

/**
 * The count-in length, in bars of the current meter. It is fixed: there is
 * no length setting. The engine takes a beat count so it stays one place to
 * change.
 */
export const DEFAULT_COUNT_IN_BARS = 1;

/** The range of beat counts the engine accepts. */
export const MIN_COUNT_IN_BEATS = 1;
export const MAX_COUNT_IN_BEATS = 16;

/**
 * How far after "now" the first count-in click is placed when playback
 * starts from a stop. It must be above zero so the metronome can queue the
 * first click on the audio clock instead of playing it late, and below the
 * metronome's schedule-ahead window (0.2 s) so the first click is queued on
 * the scheduler's very first pass.
 */
export const COUNT_IN_LEAD_SECS = 0.1;

/** Positions closer than this (in bars) count as the same position. */
const SAME_POSITION_BARS = 1e-9;

export interface CountInClick {
  /** The click's beat on the metronome's beat grid (beat 0 is a downbeat). */
  beatIndex: number;
  /** Where the click falls, in grid bars. */
  atBars: number;
  /** Beat 1 of a bar: the accented click. */
  isDownbeat: boolean;
  /** Clicks still to come after this one, before the entry (0 on the last). */
  clicksLeftAfter: number;
}

export interface CountInPlan {
  /** The clicks in order, earliest first. */
  clicks: CountInClick[];
  /** The first click, in grid bars. */
  firstClickBars: number;
  /** The entry the count-in leads into, in grid bars. */
  entryBars: number;
  /** From the first click to the entry, in bars. */
  lengthBars: number;
}

/** One bar of the meter's beats: the count-in. */
export const defaultCountInBeats = (timeSignature: TimeSignature): number =>
  beatsPerBar(timeSignature) * DEFAULT_COUNT_IN_BARS;

const assertCountInBeats = (beats: number): void => {
  if (!Number.isInteger(beats) || beats < MIN_COUNT_IN_BEATS || beats > MAX_COUNT_IN_BEATS) {
    throw new Error(
      `A count-in is ${MIN_COUNT_IN_BEATS} to ${MAX_COUNT_IN_BEATS} whole beats, not ${beats}`,
    );
  }
};

/**
 * The clicks of a count-in of `beats` beats that leads into `entryBars`.
 * Throws when the meter is not playable or `beats` is out of range.
 */
export const planCountIn = ({
  timeSignature,
  entryBars,
  beats,
}: {
  timeSignature: TimeSignature;
  entryBars: number;
  beats: number;
}): CountInPlan => {
  assertCountInBeats(beats);
  if (!Number.isFinite(entryBars)) {
    throw new Error(`A count-in needs a finite entry position, not ${entryBars}`);
  }
  const grid = beatGridOf(timeSignature);
  // beatIndexAtOrBefore works in seconds for a bar of a given length; with a
  // one-second bar, seconds and bars are the same number. The nudge keeps an
  // entry that float arithmetic put a hair before its beat on that beat.
  const beatAtOrBeforeEntry = beatIndexAtOrBefore(entryBars + SAME_POSITION_BARS, 1, grid);
  const entryIsOnThatBeat =
    Math.abs(beatStartInBars(beatAtOrBeforeEntry, grid) - entryBars) < SAME_POSITION_BARS;
  const lastClickBeat = entryIsOnThatBeat ? beatAtOrBeforeEntry - 1 : beatAtOrBeforeEntry;
  const firstClickBeat = lastClickBeat - beats + 1;
  const clicks = Array.from({ length: beats }, (_unused, offset): CountInClick => {
    const beatIndex = firstClickBeat + offset;
    return {
      beatIndex,
      atBars: beatStartInBars(beatIndex, grid),
      isDownbeat: isDownbeat(beatIndex, grid),
      clicksLeftAfter: beats - 1 - offset,
    };
  });
  const firstClickBars = clicks[0].atBars;
  return {
    clicks,
    firstClickBars,
    entryBars,
    lengthBars: entryBars - firstClickBars,
  };
};

/**
 * When the transport should reach `startBars` (where playback starts, in grid
 * bars) so the count-in's first click lands on `firstClickAudioTime`. When
 * the count-in would start after `startBars` (rests before the entry are
 * longer than the count-in), there is nothing to wait for: the transport
 * starts at `firstClickAudioTime` and the rests click as ordinary bars.
 */
export const transportStartAudioTime = ({
  plan,
  startBars,
  firstClickAudioTime,
  measureDurationSecs,
}: {
  plan: CountInPlan;
  startBars: number;
  firstClickAudioTime: number;
  measureDurationSecs: number;
}): number =>
  firstClickAudioTime + Math.max(0, startBars - plan.firstClickBars) * measureDurationSecs;

/**
 * Where playback resumes after a long pause ("Still playing?"): the count-in
 * length (one bar) before where it stopped, so the player hears a bar again
 * after the count-in; less than a bar in, it starts from the beginning.
 * Positions in transport measures; `firstMeasure` is where the piece (or the
 * loop) starts.
 */
export const countInJumpBackMeasure = ({
  pausedAtMeasure,
  firstMeasure = 0,
}: {
  pausedAtMeasure: number;
  firstMeasure?: number;
}): number => {
  const jumpedBack = pausedAtMeasure - DEFAULT_COUNT_IN_BARS;
  return jumpedBack < firstMeasure ? firstMeasure : jumpedBack;
};
